import React, { useState } from "react";
import Select from "react-select";
import PropType from "prop-types";
import { colors, typography } from "../../styles/theme";

const colourStyles = (customStyles = {}) => {
  return {
    container: (styles) => ({
      ...styles,
      width: "100%",
      ...(customStyles?.container || {}),
    }),
    control: (styles, state) => {
      return {
        ...styles,
        height: "3rem",
        border: `1px solid ${colors.neutral300}`,
        outline: "none",
        boxShadow: "none",
        borderRadius: "0.625rem",
        padding: "0.625rem 0.875rem",
        ...(state?.menuIsOpen
          ? {
              ".region-container": { color: colors.primaryMain },
              svg: {
                stroke: colors.primaryMain,
              },
            }
          : {}),
        ":hover": {
          ...styles[":hover"],
          outline: "none",
          boxShadow: "none",
          borderColor: colors.neutral200,
          cursor: "pointer",
          ...(customStyles?.control?.hover || {}),
        },
        ":selected": {
          outline: "none",
          boxShadow: "none",
          borderColor: colors.neutral200,
        },
        ":focus": {
          ...styles[":focus"],
          outline: "none",
          boxShadow: "none",
          borderColor: colors.neutral200,
        },
        ":active": {
          ...styles[":active"],
          outline: "none",
          boxShadow: "none",
          borderColor: colors.neutral200,
        },
        ...(customStyles?.control || {}),
      };
    },
    menu: (styles) => ({
      ...styles,
      border: `1px solid ${colors.neutral300}`,
      borderRadius: 0,
      boxShadow: "none",
      ...(customStyles?.menu || {}),
    }),
    menuList: (styles) => ({
      ...styles,
      paddingTop: 0,
      paddingBottom: 0,
      ...(customStyles?.menuList || {}),
    }),
    option: (styles, { isDisabled, isFocused, isSelected, options, value }) => {
      const lastOption = options[options?.length - 1]?.value === value;
      return {
        ...styles,
        borderBottom: lastOption ? "none" : `1px solid ${colors.neutral200}`,
        padding: "1rem 0.75rem",
        ...typography.textMd400,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? colors.primary100
          : isFocused
          ? undefined
          : undefined,
        color: isDisabled
          ? undefined
          : isSelected
          ? colors.primaryMain
          : isFocused
          ? undefined
          : undefined,
        cursor: isDisabled ? "not-allowed" : "pointer",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? colors.primary100
              : colors.neutral200
            : undefined,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: !isDisabled ? colors.primary100 : colors.neutral200,
          color: !isDisabled ? colors.primaryMain : undefined,
        },
        ...(customStyles?.option || {}),
      };
    },
    input: (styles) => ({
      ...styles,
      ...typography.textMd500,
      margin: 0,
      padding: 0,
      ...(customStyles?.input || {}),
    }),
    placeholder: (styles) => ({
      ...styles,
      ...typography.textMd500,
      color: colors.neutral900,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      margin: 0,
      ...(customStyles?.placeholder || {}),
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none",
      ...(customStyles?.indicatorSeparator || {}),
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      ...(customStyles?.indicatorsContainer || {}),
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: 0,
      ...(customStyles?.valueContainer || {}),
    }),
    dropdownIndicator: (styles, state) => ({
      ...styles,
      color: colors.primaryMain,
      padding: 0,
      width: "1.5rem",
      transform: state?.selectProps?.menuIsOpen
        ? "rotate(180deg)"
        : "rotate(0deg)",
      transition: "transform 0.1s ease",
      ":hover": {
        color: colors.primaryMain,
      },
      ...(customStyles?.dropdownIndicator || {}),
    }),
    singleValue: (styles) => ({
      ...styles,
      ...typography.textMd500,
      ...(customStyles?.singleValue || {}),
    }),
  };
};

const Dropdown = ({
  options,
  placeholder,
  onChange,
  customStyles,
  disabled,
  ...props
}) => {
  const [selectedOption, setSelectedOption] = useState();

  const handleSelect = (selectedOption) => {
    setSelectedOption(selectedOption);
    onChange(selectedOption);
  };

  return (
    <Select
      isDisabled={disabled}
      options={options}
      value={options.find((option) => option?.value === selectedOption?.value)}
      placeholder={placeholder}
      onChange={handleSelect}
      menuShouldScrollIntoView={true}
      styles={colourStyles(customStyles)}
      {...props}
    />
  );
};

Dropdown.propTypes = {
  options: PropType.array,
  placeholder: PropType.string,
  onChange: PropType.func,
  customStyles: PropType.object,
  disabled: PropType.bool,
};

export default Dropdown;
